import { jsx as _jsx } from "react/jsx-runtime";

/* eslint-disable promise/catch-or-return */
import { useEffect, useState, useRef } from 'react';
import UIButton from 'UIComponents/button/UIButton';
import UISelectableBox from 'UIComponents/button/UISelectableBox';
import UpgradeProductProperties, { UpgradeProductPropertiesFromApiName } from 'self-service-api/constants/UpgradeProductProperties';
import { getUpgradeButtonText, getUpgradeButtonTextFromApiName } from 'ui-addon-upgrades/_core/common/adapters/getUpgradeButtonText';
import { track } from 'ui-addon-upgrades/_core/common/eventTracking/tracker';
import { getCorrectedUpgradeData } from '../_core/utils/getCorrectedUpgradeData';
import { PricingPageRedirectButtonPropsInterface } from './interfaces/pricingPageRedirectButtonInterface';
import { omit } from 'ui-addon-upgrades/utils'; // @ts-expect-error dependency missing types

import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getProducts } from 'self-service-api/api/getProducts';
import { getSkuIdFromApiName } from '../_core/utils/productUtils';
import { getProductInfoFromSkuId } from 'self-service-api/core/utilities/productUtils';
import { getSource, mapPurchaseMotionApiName, trackPotentialBannerInteraction } from 'ui-addon-upgrades/ums/utils';
import { getUpgradeDataTrackingProperties } from 'ui-addon-upgrades/ums/utils';
import { productsAndAddons, suiteStarter } from 'self-service-api/core/utilities/links';
import { SUITE_STARTER, SUITE_PROFESSIONAL, SUITE_ENTERPRISE } from 'self-service-api/constants/UpgradeProducts';
import Raven from 'raven-js';
import { trackFailedApiNameMap } from '../_core/utils/metricUtils';
import { CRM_SUITE_ENTERPRISE_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_STARTER_BUNDLE } from 'self-service-api/constants/ApiNames';
import { getCanViewNewSeats } from '../_core/common/eligibility/getCanViewNewSeats';

const UIPricingPageRedirectButton = props => {
  const _isMounted = useRef(true);

  const {
    upgradeData,
    usageLimitBannerData,
    isSelectableBox,
    subscreen,
    children,
    isUpgradePoint,
    onClick,
    level,
    goToSuiteStaterInterstitial,
    canViewNewSeats
  } = props;
  const upgradeDataWithApiName = Object.assign({}, upgradeData, {
    apiName: mapPurchaseMotionApiName(canViewNewSeats, upgradeData.upgradeProduct, 'UIPricingPageRedirectButton', upgradeData.apiName)
  });
  const correctedUpgradeData = getCorrectedUpgradeData(upgradeDataWithApiName);
  const [hasTrackedView, setHasTrackedView] = useState(false);
  const [fetchedCanViewNewSeats, setFetchedCanViewNewSeats] = useState(canViewNewSeats);
  useEffect(() => {
    if (typeof canViewNewSeats === 'undefined' && typeof fetchedCanViewNewSeats === 'undefined') {
      getCanViewNewSeats().then(res => {
        setFetchedCanViewNewSeats(res);
      }).catch(() => {
        setFetchedCanViewNewSeats(false);
      });
    }
  });
  const propsToPass = omit(props, 'usageLimitBannerData', 'isSelectableBox', 'upgradeData', 'subscreen', 'isUpgradePoint', 'onClick', 'level', 'goToSuiteStaterInterstitial', 'canViewNewSeats');
  const source = getSource(correctedUpgradeData.app, correctedUpgradeData.screen, correctedUpgradeData.uniqueId);

  const getUpgradeLink = () => {
    if (canViewNewSeats && correctedUpgradeData.apiName) {
      const mappedProperties = UpgradeProductPropertiesFromApiName[correctedUpgradeData.apiName];
      return mappedProperties && mappedProperties.upgradeLink ? mappedProperties.upgradeLink : undefined;
    }

    return (UpgradeProductProperties[correctedUpgradeData.upgradeProduct] || {}).upgradeLink;
  };

  const upgradeLink = getUpgradeLink();
  const [products, setProducts] = useState();
  useEffect(() => {
    if ((typeof fetchedCanViewNewSeats !== 'undefined' || typeof canViewNewSeats !== 'undefined') && !hasTrackedView) {
      correctedUpgradeData.apiName = mapPurchaseMotionApiName(fetchedCanViewNewSeats || canViewNewSeats, correctedUpgradeData.upgradeProduct, 'UIPricingPageRedirectButton', correctedUpgradeData.apiName);
      track('pricingPageRedirectButtonInteraction', {
        action: 'viewed',
        upgradeProduct: correctedUpgradeData.upgradeProduct,
        source,
        subscreen,
        apiName: correctedUpgradeData.apiName
      });

      if (isUpgradePoint) {
        track('interaction', Object.assign({
          action: 'viewed pricing page redirect',
          isPrimaryCta: level === 1,
          isSecondaryCta: level === 2
        }, usageLimitBannerData, {}, getUpgradeDataTrackingProperties(correctedUpgradeData, isUpgradePoint)));
      }

      setHasTrackedView(true);
    }
  }, [correctedUpgradeData, source, subscreen, isUpgradePoint, level, usageLimitBannerData, fetchedCanViewNewSeats, canViewNewSeats, hasTrackedView]);
  useEffect(() => {
    if (_isMounted.current) {
      getProducts().then(response => {
        if (_isMounted.current) {
          setProducts(response);
        }
      });
    }
  }, [setProducts]);
  useEffect(() => {
    return function cleanup() {
      _isMounted.current = false;
    };
  }, []);
  const apiNameToUse = fetchedCanViewNewSeats && correctedUpgradeData.apiName || UpgradeProductToApiNameMap[correctedUpgradeData.upgradeProduct];

  const getUpgradeText = () => {
    if (fetchedCanViewNewSeats && correctedUpgradeData.apiName) {
      return getUpgradeButtonTextFromApiName('upgrade', correctedUpgradeData.apiName);
    }

    return getUpgradeButtonText('upgrade', correctedUpgradeData.upgradeProduct);
  };

  const getRedirectLink = () => {
    // For ebx-633 suite starter interstitial page
    // this is also used in the UMS banners
    if (goToSuiteStaterInterstitial) {
      return suiteStarter(correctedUpgradeData.uniqueId);
    }

    if (!products || !apiNameToUse) {
      if (!apiNameToUse) {
        trackFailedApiNameMap('getRedirectLink', correctedUpgradeData.apiName || 'no api name', correctedUpgradeData.upgradeProduct);
        Raven.captureMessage(`failed to get redirect link from apiName ${correctedUpgradeData.apiName} in getRedirectLink`);
      } // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore


      return upgradeLink(correctedUpgradeData.uniqueId);
    }

    const productInfo = getProductInfoFromSkuId(products, getSkuIdFromApiName(products, apiNameToUse));

    const productIsSuiteProduct = () => {
      if (fetchedCanViewNewSeats && correctedUpgradeData.apiName) {
        return [CRM_SUITE_STARTER_BUNDLE, CRM_SUITE_PROFESSIONAL_BUNDLE, CRM_SUITE_ENTERPRISE_BUNDLE].includes(correctedUpgradeData.apiName);
      }

      return [SUITE_STARTER, SUITE_PROFESSIONAL, SUITE_ENTERPRISE].includes(correctedUpgradeData.upgradeProduct);
    };

    const getCrmSuiteUpgradeLink = () => {
      if (fetchedCanViewNewSeats && correctedUpgradeData.apiName) {
        const mappedProperties = UpgradeProductPropertiesFromApiName[correctedUpgradeData.apiName];

        if (mappedProperties && mappedProperties.upgradeLink) {
          return mappedProperties.upgradeLink(correctedUpgradeData.uniqueId, correctedUpgradeData.tier);
        }
      }

      return UpgradeProductProperties[correctedUpgradeData.upgradeProduct].upgradeLink( // @ts-expect-error ts-issue
      correctedUpgradeData.uniqueId, correctedUpgradeData.tier);
    };

    if (productInfo && upgradeLink) {
      const {
        productLevel
      } = productInfo;
      return upgradeLink(correctedUpgradeData.uniqueId, productLevel);
    } else if (productIsSuiteProduct() === true) {
      return getCrmSuiteUpgradeLink();
    }

    return productsAndAddons(); //fallback generic pricing page link
  };

  const handleUpgrade = () => {
    track('pricingPageRedirectButtonInteraction', {
      action: 'clicked',
      upgradeProduct: correctedUpgradeData.upgradeProduct,
      source,
      subscreen,
      apiName: correctedUpgradeData.apiName
    });

    if (isUpgradePoint) {
      track('interaction', Object.assign({
        action: 'clicked upgrade point'
      }, usageLimitBannerData, {}, getUpgradeDataTrackingProperties(correctedUpgradeData, isUpgradePoint)));
      track('interaction', Object.assign({
        action: 'clicked pricing page redirect',
        isPrimaryCta: level === 1,
        isSecondaryCta: level === 2
      }, usageLimitBannerData, {}, getUpgradeDataTrackingProperties(correctedUpgradeData, isUpgradePoint)));
      const {
        pointType,
        upgradeKey
      } = correctedUpgradeData;
      trackPotentialBannerInteraction(pointType, upgradeKey);
    }

    if (onClick) onClick();
  };

  if (!upgradeLink) return null;

  if (isSelectableBox) {
    return /*#__PURE__*/_jsx(UISelectableBox, Object.assign({
      onClick: handleUpgrade
    }, propsToPass, {
      children: children
    }));
  }

  return /*#__PURE__*/_jsx(UIButton, Object.assign({
    target: "_blank",
    "data-reagan-primary-cta": true,
    "data-selenium": "pricing-page-redirect-button",
    "data-test-id": "pricing-page-redirect-button",
    onClick: handleUpgrade,
    href: getRedirectLink(),
    external: true,
    iconProps: {
      className: 'hidden'
    }
  }, propsToPass, {
    children: children || getUpgradeText()
  }));
};

UIPricingPageRedirectButton.defaultProps = {
  use: 'primary',
  isSelectableBox: false // only use this prop when absolutely needed

};
UIPricingPageRedirectButton.propTypes = PricingPageRedirectButtonPropsInterface;
export default UIPricingPageRedirectButton;